/**
 * ChannelOverrideEditor
 *
 * Per-channel AI prompt override management with smart presets.
 * Wires to Rod's AM-4013 backend: GET/PUT AIConfiguration/{id}/channel-overrides
 *
 * Features:
 *   - Checkbox presets per channel (auto-generate prompt instructions)
 *   - Auto-detect enabled channels from subscription → suggest relevant overrides
 *   - Capability awareness: knows if bridging, SMS, email are available
 *   - Cross-channel intelligence presets (IVR can offer SMS link, etc.)
 *   - Freeform text for advanced customization on top of presets
 *
 * 3-layer prompt assembly:
 *   Layer 1: Global prompt (site-wide)
 *   Layer 2: Bot prompt (AIConfiguration.Prompt)
 *   Layer 3: Channel override (this editor — presets + custom text)
 */
import * as React from "react";
import { useState, useEffect, useCallback, useMemo } from "react";
import useApi from "@/hooks/useApi";
import useSubscriptions from "@/hooks/useSubscriptions";
import { channelDefs, Channel } from "@/types";
import {
  ChannelOverride,
  OverrideMode,
  CHANNEL_SOURCE_MAP,
  SOURCE_CHANNEL_MAP,
  OVERRIDE_MODE_LABELS,
} from "./channelOverrideTypes";

interface Props {
  aiConfigId: number;
  botPrompt?: string;
  globalPrompt?: string;
  /** Which channels are active on the workflow trigger (if known) */
  workflowChannels?: string[];
  onClose?: () => void;
}

// ── Preset definitions per channel ─────────────────────────────────────────
interface ChannelPreset {
  id: string;
  label: string;
  description: string;
  promptText: string;
  /** Only show this preset if these subscription flags are true */
  requiresCapability?: string[];
  /** Only show if these other channels are also enabled */
  requiresOtherChannel?: string[];
  /** Auto-check this preset when the channel is first expanded */
  recommended?: boolean;
}

const CHANNEL_PRESETS: Record<string, ChannelPreset[]> = {
  twilioIvr: [
    {
      id: "ivr_no_urls",
      label: "Don't read URLs aloud",
      description: "Prevents the bot from reading out http:// links via TTS",
      promptText: "Never read out full URLs or web addresses. If the visitor needs a link, offer to send it via SMS or email instead.",
      recommended: true,
    },
    {
      id: "ivr_numbered_menus",
      label: "Use numbered menus",
      description: "Present options as 'Press 1 for..., Press 2 for...'",
      promptText: "When presenting options, use numbered menus (say 1 for..., say 2 for...). Limit options to 5 or fewer per menu. Wait for the caller to respond before continuing.",
      recommended: true,
    },
    {
      id: "ivr_concise",
      label: "Keep responses concise",
      description: "Shorter responses sound better via text-to-speech",
      promptText: "Keep responses under 3 sentences. Callers are listening, not reading — be direct and clear. Avoid jargon, bullet points, and formatted text.",
      recommended: true,
    },
    {
      id: "ivr_offer_sms",
      label: "Offer to send info via SMS",
      description: "When the caller needs a link or detailed info, offer SMS delivery",
      promptText: "If the caller needs a link, confirmation number, tracking info, or detailed instructions, ask: 'Would you like me to send that to your phone via text message?' If they agree, use the SMS capability to deliver it.",
      requiresOtherChannel: ["twilioSms"],
      recommended: true,
    },
    {
      id: "ivr_offer_email",
      label: "Offer to send info via email",
      description: "When the caller needs documents or detailed info, offer email",
      promptText: "If the caller needs a document, receipt, or detailed written information, offer to send it via email. Ask for their email address and confirm before sending.",
      requiresOtherChannel: ["email"],
    },
    {
      id: "ivr_transfer_human",
      label: "Offer live agent transfer",
      description: "Let callers know they can speak to a human",
      promptText: "If the caller asks to speak with a person, seems frustrated, or the issue is too complex, say: 'I can connect you with a live agent. Would you like me to transfer you now?' Transfer immediately when requested — don't try to resolve it yourself.",
    },
    {
      id: "ivr_no_emoji",
      label: "No emoji or special characters",
      description: "TTS engines mangle emoji and special chars",
      promptText: "Never use emoji, asterisks, bullet points, or special formatting characters. Use plain text only — everything you write will be read aloud by a text-to-speech engine.",
    },
  ],
  twilioSms: [
    {
      id: "sms_concise",
      label: "Keep under 160 characters",
      description: "Single SMS segment — no extra charges",
      promptText: "Keep each response under 160 characters whenever possible. If you must send more, break it into clear separate messages rather than one long message.",
      recommended: true,
    },
    {
      id: "sms_numbered_options",
      label: "Use numbered reply options",
      description: "Reply 1, 2, 3 instead of clickable buttons",
      promptText: "When presenting choices, use numbered options: '1. Option A\\n2. Option B\\n3. Option C\\nReply with a number to select.' Limit to 5 options maximum.",
      recommended: true,
    },
    {
      id: "sms_links_short",
      label: "Use short links sparingly",
      description: "Links work but eat into character limits",
      promptText: "You can include links in SMS but keep them short and use at most one per message. Never include more than one URL in a single text.",
    },
    {
      id: "sms_no_formatting",
      label: "No markdown or rich formatting",
      description: "SMS is plain text only",
      promptText: "Do not use markdown, bold, italic, headers, or bullet points. SMS is plain text only. Use line breaks and numbered lists for structure.",
      recommended: true,
    },
    {
      id: "sms_offer_call",
      label: "Offer to call for complex issues",
      description: "Suggest a phone call when text is insufficient",
      promptText: "If the issue is complex and hard to resolve via text, offer: 'This might be easier to handle over the phone. Would you like us to call you?' Respect the visitor's preference if they want to stay on SMS.",
      requiresOtherChannel: ["twilioIvr"],
    },
  ],
  whatsApp: [
    {
      id: "wa_rich_text",
      label: "Use WhatsApp formatting",
      description: "Bold, italic, and numbered lists work well",
      promptText: "You can use *bold* for emphasis and numbered lists for options. WhatsApp supports rich text — use it to make responses scannable.",
    },
    {
      id: "wa_numbered_options",
      label: "Use numbered reply options",
      description: "Numbered lists are the most reliable for selection",
      promptText: "When presenting choices, use numbered options that the visitor can reply with. Example: '1. Check order status\\n2. Return an item\\n3. Speak to an agent'",
      recommended: true,
    },
    {
      id: "wa_media_aware",
      label: "Acknowledge media capabilities",
      description: "WhatsApp supports images, documents, and links",
      promptText: "You can share links, and the visitor can send you images or documents. If they need to show you something (e.g., a damaged item, a screenshot), ask them to send a photo.",
    },
  ],
  email: [
    {
      id: "email_detailed",
      label: "Allow longer, detailed responses",
      description: "Email readers expect thorough answers",
      promptText: "Email responses can be more detailed than chat. Include relevant context, step-by-step instructions, and links. Use a professional but friendly tone with proper greeting and sign-off.",
    },
    {
      id: "email_formatting",
      label: "Use proper email formatting",
      description: "Headers, bullet points, and paragraphs",
      promptText: "Structure email responses with clear paragraphs, bullet points for lists, and bold for key information. Include a greeting and sign-off. Make it easy to scan.",
    },
    {
      id: "email_rate_limit",
      label: "Limit follow-up emails",
      description: "Don't send excessive automated emails",
      promptText: "Do not send more than 2 follow-up emails in a conversation. If the visitor hasn't responded after 2 messages, stop and wait. Never send emails to addresses the visitor didn't provide.",
      recommended: true,
    },
  ],
  web: [
    {
      id: "web_buttons",
      label: "Use clickable buttons for options",
      description: "Web chat supports interactive buttons and cards",
      promptText: "When presenting options, use clickable buttons rather than numbered lists. Web chat supports rich interactive elements — take advantage of them.",
    },
  ],
  facebook: [
    {
      id: "fb_quick_replies",
      label: "Use quick reply buttons",
      description: "Messenger supports quick reply chips",
      promptText: "Use quick reply options when presenting choices. Keep responses conversational — Facebook Messenger users expect a casual, friendly tone.",
      recommended: true,
    },
    {
      id: "fb_concise",
      label: "Keep responses short",
      description: "Messenger users expect quick, chatty responses",
      promptText: "Keep responses under 2-3 sentences. Messenger is a conversational platform — break longer information into multiple short messages rather than one wall of text.",
    },
  ],
  twitter: [
    {
      id: "tw_brief",
      label: "Keep responses very brief",
      description: "Twitter DMs expect concise communication",
      promptText: "Keep responses extremely concise — 1-2 sentences maximum. If more detail is needed, offer to continue via email or a support link.",
      recommended: true,
    },
  ],
};

// ── Smart suggestion logic ────────────────────────────────────────────────

interface ChannelSuggestion {
  channel: string;
  reason: string;
  presetIds: string[];
}

function getSmartSuggestions(
  enabledChannels: string[],
  existingOverrides: Record<string, ChannelOverride>
): ChannelSuggestion[] {
  const suggestions: ChannelSuggestion[] = [];

  // IVR + SMS combo → recommend IVR override with SMS offer
  if (enabledChannels.includes("twilioIvr") && enabledChannels.includes("twilioSms") && !existingOverrides["twilioIvr"]) {
    suggestions.push({
      channel: "twilioIvr",
      reason: "You have both IVR and SMS — callers can get links and details via text",
      presetIds: ["ivr_no_urls", "ivr_numbered_menus", "ivr_concise", "ivr_offer_sms", "ivr_no_emoji"],
    });
  } else if (enabledChannels.includes("twilioIvr") && !existingOverrides["twilioIvr"]) {
    suggestions.push({
      channel: "twilioIvr",
      reason: "IVR callers hear responses via TTS — needs formatting adjustments",
      presetIds: ["ivr_no_urls", "ivr_numbered_menus", "ivr_concise", "ivr_no_emoji"],
    });
  }

  // SMS without override
  if (enabledChannels.includes("twilioSms") && !existingOverrides["twilioSms"]) {
    suggestions.push({
      channel: "twilioSms",
      reason: "SMS has character limits — responses need to be concise",
      presetIds: ["sms_concise", "sms_numbered_options", "sms_no_formatting"],
    });
  }

  return suggestions;
}

// ── Component ─────────────────────────────────────────────────────────────

export default function ChannelOverrideEditor({ aiConfigId, botPrompt, globalPrompt, workflowChannels, onClose }: Props) {
  const api = useApi();
  const { subscription } = useSubscriptions();
  const [overrides, setOverrides] = useState<Record<string, ChannelOverride>>({});
  const [selectedPresets, setSelectedPresets] = useState<Record<string, Set<string>>>({});
  const [customText, setCustomText] = useState<Record<string, string>>({});
  const [expandedChannel, setExpandedChannel] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [previewChannel, setPreviewChannel] = useState<string | null>(null);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set());

  // Determine which channels are enabled based on subscription
  const enabledChannels = useMemo(() => {
    const channels: string[] = [];
    if (subscription?.enableWeb !== false) channels.push("web");
    if (subscription?.enableSms) channels.push("twilioSms");
    if (subscription?.enableIvr) channels.push("twilioIvr");
    if (subscription?.enableWhatsapp) channels.push("whatsApp");
    if (subscription?.enableEmail) channels.push("email");
    if (subscription?.enableFacebook) channels.push("facebook");
    return channels;
  }, [subscription]);

  // Smart suggestions based on enabled channels
  const suggestions = useMemo(
    () => getSmartSuggestions(enabledChannels, overrides).filter((s) => !dismissedSuggestions.has(s.channel)),
    [enabledChannels, overrides, dismissedSuggestions]
  );

  const loadOverrides = useCallback(async () => {
    try {
      const response = await api.messaging.get(`AIConfiguration/${aiConfigId}/channel-overrides`);
      if (response.ok) {
        const data = await response.json();
        const map: Record<string, ChannelOverride> = {};
        if (Array.isArray(data)) {
          for (const item of data) {
            const channelKey = SOURCE_CHANNEL_MAP[item.conversationSource] ?? item.conversationSource;
            map[channelKey] = item;
          }
        }
        setOverrides(map);
        // TODO: parse existing overrideText back into presets (future enhancement)
      }
    } catch {
      // Backend might not have this endpoint yet — safe default
    } finally {
      setLoading(false);
    }
  }, [api.messaging, aiConfigId]);

  useEffect(() => {
    loadOverrides();
  }, [loadOverrides]);

  // Build the override text from selected presets + custom text
  function buildOverrideText(channelKey: string): string {
    const presetIds = selectedPresets[channelKey];
    const custom = customText[channelKey]?.trim() ?? "";
    const channelPresets = CHANNEL_PRESETS[channelKey] ?? [];

    const parts: string[] = [];
    if (presetIds?.size) {
      for (const preset of channelPresets) {
        if (presetIds.has(preset.id)) {
          parts.push(preset.promptText);
        }
      }
    }
    if (custom) {
      parts.push(custom);
    }
    return parts.join("\n\n");
  }

  // Sync override text whenever presets or custom text change
  function syncOverrideText(channelKey: string, presetUpdate?: Set<string>, customUpdate?: string) {
    const presetIds = presetUpdate ?? selectedPresets[channelKey] ?? new Set<string>();
    const custom = customUpdate ?? customText[channelKey] ?? "";
    const channelPresets = CHANNEL_PRESETS[channelKey] ?? [];

    const parts: string[] = [];
    for (const preset of channelPresets) {
      if (presetIds.has(preset.id)) {
        parts.push(preset.promptText);
      }
    }
    if (custom.trim()) {
      parts.push(custom.trim());
    }

    const text = parts.join("\n\n");
    if (text) {
      updateOverride(channelKey, { overrideText: text, mode: "additive" });
    } else {
      // No presets and no custom text — remove override
      removeOverride(channelKey);
    }
  }

  function togglePreset(channelKey: string, presetId: string) {
    setSelectedPresets((prev) => {
      const current = new Set(prev[channelKey] ?? []);
      if (current.has(presetId)) {
        current.delete(presetId);
      } else {
        current.add(presetId);
      }
      const next = { ...prev, [channelKey]: current };
      // Sync the text
      syncOverrideText(channelKey, current);
      return next;
    });
  }

  function handleCustomTextChange(channelKey: string, text: string) {
    setCustomText((prev) => ({ ...prev, [channelKey]: text }));
    syncOverrideText(channelKey, undefined, text);
  }

  function applySuggestion(suggestion: ChannelSuggestion) {
    const presets = new Set(suggestion.presetIds);
    setSelectedPresets((prev) => ({ ...prev, [suggestion.channel]: presets }));
    setExpandedChannel(suggestion.channel);
    syncOverrideText(suggestion.channel, presets);
    setDismissedSuggestions((prev) => new Set([...prev, suggestion.channel]));
  }

  function updateOverride(channelKey: string, updates: Partial<ChannelOverride>) {
    setOverrides((prev) => {
      const existing = prev[channelKey];
      const base: ChannelOverride = {
        conversationSource: CHANNEL_SOURCE_MAP[channelKey] ?? channelKey,
        mode: "additive",
        overrideText: "",
      };
      return {
        ...prev,
        [channelKey]: { ...base, ...existing, ...updates },
      };
    });
  }

  function removeOverride(channelKey: string) {
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[channelKey];
      return next;
    });
    setSelectedPresets((prev) => {
      const next = { ...prev };
      delete next[channelKey];
      return next;
    });
    setCustomText((prev) => {
      const next = { ...prev };
      delete next[channelKey];
      return next;
    });
  }

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    try {
      const payload = Object.entries(overrides)
        .filter(([, ov]) => ov.mode && (ov.mode === "suppress" || ov.overrideText?.trim()))
        .map(([channelKey, ov]) => ({
          conversationSource: CHANNEL_SOURCE_MAP[channelKey] ?? channelKey,
          mode: ov.mode,
          overrideText: ov.overrideText ?? "",
        }));

      await api.messaging.put(`AIConfiguration/${aiConfigId}/channel-overrides`, payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // silently fail — endpoint may not exist yet
    } finally {
      setSaving(false);
    }
  }

  function assemblePreview(channelKey: string): string {
    const override = overrides[channelKey];
    const parts: string[] = [];

    if (globalPrompt?.trim()) {
      parts.push(`[Global Prompt]\n${globalPrompt.trim()}`);
    }

    if (!override || override.mode === "additive") {
      if (botPrompt?.trim()) {
        parts.push(`[Bot Prompt]\n${botPrompt.trim()}`);
      }
      if (override?.overrideText?.trim()) {
        parts.push(`[${channelDefs[channelKey as Channel] ?? channelKey} Override — Additive]\n${override.overrideText.trim()}`);
      }
    } else if (override.mode === "replace") {
      parts.push(`[${channelDefs[channelKey as Channel] ?? channelKey} Override — Replaces Bot Prompt]\n${override.overrideText?.trim() ?? ""}`);
    } else if (override.mode === "suppress") {
      parts.push(`[Bot Prompt — Suppressed]`);
    }

    return parts.join("\n\n") || "(No prompt content)";
  }

  // Filter presets based on available capabilities
  function getAvailablePresets(channelKey: string): ChannelPreset[] {
    const allPresets = CHANNEL_PRESETS[channelKey] ?? [];
    return allPresets.filter((preset) => {
      // Check required other channels
      if (preset.requiresOtherChannel) {
        const hasRequired = preset.requiresOtherChannel.some((ch) => enabledChannels.includes(ch));
        if (!hasRequired) return false;
      }
      return true;
    });
  }

  const configuredCount = Object.keys(overrides).length;

  if (loading) {
    return (
      <div className="p-4 text-center text-sm text-slate-400">
        Loading channel overrides...
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Channel Prompt Overrides</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Customize how this AI behaves on different channels
          </p>
        </div>
        <div className="flex items-center gap-2">
          {configuredCount > 0 && (
            <span className="text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded px-2 py-0.5">
              {configuredCount} channel{configuredCount !== 1 ? "s" : ""} configured
            </span>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 text-lg leading-none"
              aria-label="Close channel overrides"
            >
              &times;
            </button>
          )}
        </div>
      </div>

      {/* Smart Suggestions */}
      {suggestions.length > 0 && (
        <div className="flex flex-col gap-2">
          {suggestions.map((s) => (
            <div
              key={s.channel}
              className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5 flex items-start gap-2"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-500 flex-shrink-0 mt-0.5" aria-hidden="true">
                <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-blue-800">
                  Recommended: Set up {channelDefs[s.channel as Channel] ?? s.channel} overrides
                </p>
                <p className="text-[11px] text-blue-600 mt-0.5">{s.reason}</p>
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => applySuggestion(s)}
                    className="text-[11px] font-medium text-white bg-blue-600 hover:bg-blue-700 rounded px-2.5 py-1 transition-colors"
                  >
                    Apply Recommended Presets
                  </button>
                  <button
                    type="button"
                    onClick={() => setDismissedSuggestions((prev) => new Set([...prev, s.channel]))}
                    className="text-[11px] text-blue-500 hover:text-blue-700"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* How it works */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-600">
        <div className="font-medium text-slate-700 mb-1">How prompt assembly works</div>
        <div className="flex items-center gap-1.5 text-[11px]">
          <span className="bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">Global Prompt</span>
          <span className="text-slate-400">+</span>
          <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Bot Prompt</span>
          <span className="text-slate-400">+</span>
          <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Presets + Custom</span>
        </div>
      </div>

      {/* Channel list */}
      <div className="flex flex-col gap-1.5">
        {Object.entries(channelDefs).map(([key, label]) => {
          // Only show channels that are enabled in subscription
          if (!enabledChannels.includes(key) && key !== "web") return null;

          const hasOverride = !!overrides[key];
          const isExpanded = expandedChannel === key;
          const override = overrides[key];
          const presets = getAvailablePresets(key);
          const activePresets = selectedPresets[key] ?? new Set<string>();
          const activePresetCount = activePresets.size;

          return (
            <div
              key={key}
              className={`border rounded-lg transition-colors ${
                hasOverride
                  ? "border-blue-200 bg-blue-50/30"
                  : "border-slate-200 bg-white"
              }`}
            >
              {/* Channel row */}
              <button
                type="button"
                onClick={() => setExpandedChannel(isExpanded ? null : key)}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-slate-50/50 transition-colors rounded-lg"
                aria-expanded={isExpanded}
                aria-label={`${label} channel override`}
              >
                <span className="text-sm">{label}</span>
                {activePresetCount > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-green-100 text-green-700">
                    {activePresetCount} preset{activePresetCount !== 1 ? "s" : ""}
                  </span>
                )}
                {hasOverride && !activePresetCount && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                    override?.mode === "replace"
                      ? "bg-amber-100 text-amber-700"
                      : override?.mode === "suppress"
                      ? "bg-red-100 text-red-700"
                      : "bg-green-100 text-green-700"
                  }`}>
                    {OVERRIDE_MODE_LABELS[override?.mode ?? "additive"].label}
                  </span>
                )}
                <svg
                  className={`ml-auto w-4 h-4 text-slate-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {/* Expanded editor */}
              {isExpanded && (
                <div className="px-3 pb-3 flex flex-col gap-2.5 border-t border-slate-100">
                  {/* Preset checkboxes */}
                  {presets.length > 0 && (
                    <div className="mt-2">
                      <label className="text-xs font-medium text-slate-700 block mb-1.5">
                        Quick Presets
                      </label>
                      <div className="flex flex-col gap-1">
                        {presets.map((preset) => {
                          const isChecked = activePresets.has(preset.id);
                          return (
                            <label
                              key={preset.id}
                              className={`flex gap-2 cursor-pointer rounded border px-2.5 py-2 transition-colors ${
                                isChecked
                                  ? "border-blue-300 bg-blue-50"
                                  : "border-slate-100 bg-white hover:border-slate-200"
                              }`}
                            >
                              <input
                                type="checkbox"
                                className="w-3.5 h-3.5 mt-0.5 shrink-0 accent-blue-600"
                                checked={isChecked}
                                onChange={() => togglePreset(key, preset.id)}
                                aria-label={preset.label}
                              />
                              <div className="min-w-0">
                                <div className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
                                  {preset.label}
                                  {preset.recommended && !isChecked && (
                                    <span className="text-[9px] text-amber-600 bg-amber-50 border border-amber-200 rounded px-1 py-0.5">
                                      Recommended
                                    </span>
                                  )}
                                  {preset.requiresOtherChannel && (
                                    <span className="text-[9px] text-violet-600 bg-violet-50 border border-violet-200 rounded px-1 py-0.5">
                                      Cross-channel
                                    </span>
                                  )}
                                </div>
                                <p className="text-[11px] text-slate-400 mt-0.5">{preset.description}</p>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Custom additional text */}
                  <div className="mt-1">
                    <label className="text-xs font-medium text-slate-700 block mb-1">
                      Additional Custom Instructions <span className="font-normal text-slate-400">(optional)</span>
                    </label>
                    <textarea
                      value={customText[key] ?? ""}
                      onChange={(e) => handleCustomTextChange(key, e.target.value)}
                      placeholder={`Add any extra ${label}-specific instructions beyond the presets above...`}
                      rows={3}
                      className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 resize-y focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300"
                    />
                  </div>

                  {/* Preview toggle */}
                  <div>
                    <button
                      type="button"
                      onClick={() => setPreviewChannel(previewChannel === key ? null : key)}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                      aria-expanded={previewChannel === key}
                    >
                      {previewChannel === key ? "Hide Preview" : "Preview Assembled Prompt"}
                    </button>
                    {previewChannel === key && (
                      <pre className="mt-1.5 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded p-2.5 whitespace-pre-wrap max-h-48 overflow-y-auto">
                        {assemblePreview(key)}
                      </pre>
                    )}
                  </div>

                  {/* Remove all */}
                  {(hasOverride || activePresetCount > 0) && (
                    <button
                      type="button"
                      onClick={() => {
                        removeOverride(key);
                        setExpandedChannel(null);
                      }}
                      className="text-xs text-red-500 hover:text-red-600 font-medium self-start"
                    >
                      Clear All Overrides
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Save */}
      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {saving ? "Saving..." : "Save Channel Overrides"}
        </button>
        {saved && (
          <span className="text-xs text-green-600 font-medium">Saved</span>
        )}
      </div>
    </div>
  );
}
